import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { mondayOf, formatRange, statusLabel, todayPT, entryHours } from "@/lib/util";
import { StatusBadge } from "@/components/StatusBadge";
import { usePhases } from "@/lib/phases";
import { Check, ChevronDown, ChevronRight, ChevronLeft, AlertTriangle, Hammer, BarChart3 } from "lucide-react";
import { toast } from "sonner";

function CrewRow({ crew, projectId, projectName, weekStart, onChanged }) {
    const [expanded, setExpanded] = useState(false);
    const { phaseName } = usePhases();
    const [working, setWorking] = useState(false);
    const isSignedOff = !!crew.signed_off;
    const isApprovedWeek = crew.timecard_status === "approved" || crew.timecard_status === "exported";
    const isSubmitted = crew.timecard_status === "submitted";
    // Foreman can act once the crew has at least submitted (or if the week is already approved, they can still toggle their own signoff)
    const canAct = isSubmitted || isSignedOff || isApprovedWeek;

    const signOff = async () => {
        // The old wording ("when every foreman has signed off across every job
        // ... the week will auto-approve") buried the one thing a foreman needs
        // to know: this signs off THIS JOB only, not the person's week.
        const entries = crew.entries.length;
        if (!window.confirm(
            `Sign off ${crew.employee_name}'s hours on ${projectName}?\n\n` +
            `${crew.hours_on_this_project.toFixed(2)} hours here, across ${entries} ${entries === 1 ? "entry" : "entries"}.\n\n` +
            `This covers ${projectName} only. ${crew.employee_name}'s whole week gets approved ` +
            `automatically once every job they worked on has been signed off.`
        )) return;
        setWorking(true);
        try {
            await api.post(`/foreman/signoff`, {
                employee_id: crew.employee_id,
                project_id: projectId,
                week_start: weekStart,
            });
            toast.success(`Signed off · ${crew.employee_name} on ${projectName}`);
            onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sign-off failed");
        } finally {
            setWorking(false);
        }
    };

    const unsignOff = async () => {
        if (!window.confirm(
            `Take back your sign-off on ${crew.employee_name}'s hours for ${projectName}?\n\n` +
            `If their week had already been approved, it goes back to waiting for approval.`
        )) return;
        setWorking(true);
        try {
            await api.delete(`/foreman/signoff`, {
                data: {
                    employee_id: crew.employee_id,
                    project_id: projectId,
                    week_start: weekStart,
                },
            });
            toast(`Sign-off removed · ${crew.employee_name} on ${projectName}`);
            onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Remove failed");
        } finally {
            setWorking(false);
        }
    };

    let signoffTitle, signoffClass, onSignoffClick;
    if (isSignedOff) {
        signoffTitle = "Signed off · click to remove";
        signoffClass = "bg-brand-success border-brand-success text-white hover:bg-brand-success";
        onSignoffClick = unsignOff;
    } else if (canAct) {
        signoffTitle = `Sign off on ${crew.employee_name}'s hours for ${projectName}`;
        signoffClass = "bg-white border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-brand-bone";
        onSignoffClick = signOff;
    } else {
        signoffTitle = "Crew hasn't submitted yet";
        signoffClass = "bg-white border-brand-charcoal/15 text-brand-charcoal/30 cursor-not-allowed";
        onSignoffClick = undefined;
    }

    return (
        <div className="border border-brand-charcoal/10 rounded-xl bg-white" data-testid={`crew-row-${crew.employee_id}`}>
            <div className="flex items-center gap-3 p-3">
                <button
                    onClick={() => setExpanded((x) => !x)}
                    className="p-1.5 rounded-lg hover:bg-brand-stone text-brand-charcoal/70 shrink-0"
                    aria-label="Toggle details"
                    data-testid={`expand-${crew.employee_id}`}
                >
                    {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-bold text-brand-charcoal truncate">{crew.employee_name}</span>
                        {crew.employee_code && <span className="text-[10px] text-brand-charcoal/50">{crew.employee_code}</span>}
                    </div>
                    <div className="text-xs text-brand-charcoal/70 mt-0.5">
                        {crew.entries.length} {crew.entries.length === 1 ? "entry" : "entries"} ·
                        <span className="font-semibold text-brand-navy ml-1">{crew.hours_on_this_project.toFixed(2)}h on {projectName}</span>
                        {crew.timecard_totals?.total != null && (
                            <span className="text-brand-charcoal/60"> · {crew.timecard_totals.total.toFixed(2)}h total week</span>
                        )}
                    </div>
                    {isSignedOff && crew.signed_by_name && (
                        <div className="text-[10px] text-brand-success mt-0.5" data-testid={`signed-by-${crew.employee_id}`}>
                            Signed by {crew.signed_by_name}
                            {crew.signed_at && ` · ${dayjs(crew.signed_at).format("MMM D, h:mm A")}`}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {crew.timecard_status && <StatusBadge status={crew.timecard_status} />}
                    <button
                        onClick={onSignoffClick}
                        disabled={!canAct || working}
                        data-testid={`signoff-${crew.employee_id}`}
                        title={signoffTitle}
                        className={`h-10 w-10 rounded-lg flex items-center justify-center transition-all border-2 ${signoffClass}`}
                    >
                        {isSignedOff || working ? <Check className="h-5 w-5" /> : <span className="text-xs font-bold">OK?</span>}
                    </button>
                </div>
            </div>
            {expanded && (
                <div className="border-t border-brand-charcoal/10 px-3 py-2 bg-brand-stone/40 space-y-1 text-sm" data-testid={`entries-${crew.employee_id}`}>
                    {crew.entries.map((e) => (
                        <Link
                            key={e.entry_id}
                            to={`/foreman/timecard/${crew.timecard_id}`}
                            className="flex justify-between gap-3 py-2 px-2 rounded hover:bg-white"
                            data-testid={`entry-${e.entry_id}`}
                        >
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-widest text-brand-navy">{dayjs(e.date).format("ddd MMM D")}</div>
                                {/* The phases by name. "2 phases" told a foreman
                                    the shape of the entry and nothing about the
                                    work -- and the work is what they are signing
                                    off on. */}
                                <div className="truncate text-brand-charcoal" data-testid={`summary-phases-${e.entry_id}`}>
                                    {(e.phase_numbers || []).length
                                        ? e.phase_numbers.map((n) => phaseName(n)).join(" · ")
                                        : "—"}
                                </div>
                                {e.notes && <div className="text-xs text-brand-charcoal/60 italic truncate">"{e.notes}"</div>}
                            </div>
                            <span className="text-brand-navy font-bold shrink-0 self-center">
                                {entryHours(e).toFixed(2)}h
                            </span>
                        </Link>
                    ))}
                    <Link to={`/foreman/timecard/${crew.timecard_id}`} className="block text-center text-xs font-bold text-brand-navy py-1 hover:underline" data-testid={`open-timecard-${crew.timecard_id}`}>
                        Open full week review →
                    </Link>
                </div>
            )}
        </div>
    );
}

export default function ForemanSummary() {
    const navigate = useNavigate();
    const [week, setWeek] = useState(mondayOf(todayPT()));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async (w) => {
        setLoading(true);
        try {
            const r = await api.get(`/foreman/summary?week=${w}`);
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load summary");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(week); }, [week]); // eslint-disable-line react-hooks/exhaustive-deps

    const changeWeek = (delta) => {
        const next = dayjs(week).add(delta * 7, "day").format("YYYY-MM-DD");
        setWeek(next);
    };

    if (loading && !data) return <div className="p-6 text-brand-charcoal/60">Loading…</div>;
    if (!data) return null;

    return (
        <div className="max-w-5xl mx-auto px-4 py-4 space-y-4 pb-28" data-testid="foreman-summary">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm font-bold text-brand-foreman-header hover:underline" data-testid="back-button">
                    <ChevronLeft className="h-4 w-4" /> Back to queue
                </button>
                <div className="flex items-center gap-2">
                    <button onClick={() => changeWeek(-1)} className="p-2 rounded-lg hover:bg-brand-stone" data-testid="prev-week"><ChevronLeft className="h-5 w-5" /></button>
                    <button onClick={() => changeWeek(1)} disabled={dayjs(week).add(7, "day").isAfter(todayPT(), "day")} className="p-2 rounded-lg hover:bg-brand-stone disabled:opacity-30" data-testid="next-week"><ChevronRight className="h-5 w-5" /></button>
                </div>
            </div>

            <div className="bg-brand-foreman-header text-brand-bone rounded-2xl p-5 shadow-lift" data-testid="week-banner">
                <div className="text-sm font-bold uppercase tracking-[0.2em] text-brand-foreman-accent">Weekly Summary by Project &middot; Pay Period {data.pay_period} of 52 &middot; {data.year}</div>
                <div className="font-bold text-3xl mt-1">{formatRange(data.week_start)}</div>
                <p className="text-xs text-brand-bone/80 mt-2">
                    Tap <b>OK?</b> next to each crew member to sign off on their hours for <b>this project</b>. Once every foreman has signed off across every project a crew member worked this week, their week auto-approves.
                </p>
            </div>

            {data.projects.length === 0 && (
                <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-8 text-center" data-testid="no-projects">
                    <Hammer className="h-8 w-8 mx-auto text-brand-charcoal/40" />
                    <p className="mt-3 text-brand-charcoal/70">No projects are assigned to you. Talk to the admin to get assigned to one or more projects.</p>
                </div>
            )}

            {data.projects.map((proj) => (
                <div key={proj.project_id} className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card overflow-hidden" data-testid={`project-${proj.project_id}`}>
                    <div className="bg-brand-foreman-header text-brand-bone px-5 py-3 flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-foreman-accent">Project · {proj.job_number}</div>
                            <div className="font-bold text-2xl mt-1 truncate" data-testid={`project-name-${proj.project_id}`}>{proj.name}</div>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="text-[10px] uppercase tracking-widest text-brand-gold">Foreman</div>
                            <div className="font-bold text-sm mt-1" data-testid={`project-manager-${proj.project_id}`}>{proj.manager_name || <span className="text-brand-bone/60 italic">Unassigned</span>}</div>
                        </div>
                    </div>

                    <div className="p-3 space-y-2">
                        {proj.crew.length === 0 ? (
                            <div className="text-sm text-brand-charcoal/60 italic py-4 text-center">No crew has logged time on this job yet this week.</div>
                        ) : (
                            proj.crew.map((c) => (
                                <CrewRow
                                    key={c.employee_id}
                                    crew={c}
                                    projectId={proj.project_id}
                                    projectName={proj.name}
                                    weekStart={data.week_start}
                                    onChanged={() => load(week)}
                                />
                            ))
                        )}
                        {proj.missing.length > 0 && (
                            <div className="mt-3 bg-brand-warn/15 border border-brand-warn/40 rounded-xl p-3" data-testid={`missing-${proj.project_id}`}>
                                <div className="flex items-center gap-2 text-brand-charcoal font-bold text-xs uppercase tracking-widest">
                                    <AlertTriangle className="h-4 w-4" />
                                    Assigned but no time logged this week
                                </div>
                                <ul className="mt-1 text-sm text-brand-charcoal ml-6 list-disc">
                                    {proj.missing.map((m) => (
                                        <li key={m.employee_id}>{m.employee_name} <span className="text-brand-charcoal/70 text-xs">{m.employee_code}</span></li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            ))}

            {/* This report used to be reachable only from the Stats tab. That
                tab is gone -- four counts that now live on the queue's own
                filter chips -- so the link moves here, next to the jobs it is
                about. */}
            <Link to="/foreman/reports/hours-by-project"
                className="flex items-center gap-2 bg-white border border-brand-charcoal/15 shadow-card rounded-xl px-5 py-4 font-bold text-brand-charcoal hover:bg-brand-stone"
                data-testid="go-to-hours-report">
                <BarChart3 className="h-4 w-4 text-brand-navy" /> Hours by Project Report →
            </Link>
        </div>
    );
}
